/**
 * Recursos da Inbox que o ponteiro de uma novidade sabe apontar. Cada chave liga a um elemento do
 * web marcado com `spotlightTargetProps("<chave>")` (o teste de integridade confere). Ver
 * `spotlightTargets.js` para o significado de cada campo.
 *
 * A Inbox desenha a mesma tela em três layouts (completo, simplificado e o celular, que força o
 * simplificado) e monta duas cópias da lista e do painel de conversa (uma oculta por CSS). O
 * ponteiro aponta a cópia que está visível; um recurso que o layout atual não desenha fica
 * esperando em silêncio.
 */

const ABRIR_CONVERSA = "Abra uma conversa na lista";
const stepsFor = (what) => [{ key: "inbox.conversation-item", text: `${ABRIR_CONVERSA} para ver ${what}.` }];

const LISTA = "Lista de conversas";
const CONVERSA = "Conversa aberta";
const COMPOSITOR = "Caixa de mensagem";
const PREFERENCIAS = "Preferências da Inbox";

const ABRIR_MENU = { key: "inbox.list-menu", text: "Abra o menu de configurações no topo da lista." };
const ABRIR_PREFERENCIAS = { key: "inbox.preferences-menu", text: "Escolha Configurações gerais (ou Preferências da Inbox) no menu." };
const stepsDasPreferencias = [ABRIR_MENU, ABRIR_PREFERENCIAS];

/** Uma aba do modal de Preferências: só existe com o modal aberto, e a aba aparece conforme o papel e o plano. */
const preferencia = (slug, label, hint, extra = {}) => ({
  key: `inbox.pref-${slug}`,
  navKey: "inbox",
  group: PREFERENCIAS,
  label: `Preferências: ${label}`,
  path: "/inbox",
  requires: "panel",
  hint,
  steps: stepsDasPreferencias,
  ...extra,
});

export const INBOX_TARGETS = [
  // ── Lista de conversas ──────────────────────────────────────────────────
  {
    key: "inbox.new-message",
    navKey: "inbox",
    group: LISTA,
    label: "Nova mensagem (iniciar uma conversa)",
    path: "/inbox",
    requires: "page",
    hint: "O botão de lápis no topo da lista: inicia uma conversa enviando um template aprovado do WhatsApp.",
  },
  {
    key: "inbox.global-search",
    navKey: "inbox",
    group: LISTA,
    label: "Busca da Inbox (Ctrl+K)",
    path: "/inbox",
    requires: "page",
    hint: "Procura contatos, mensagens, arquivos e tickets de uma vez.",
  },
  {
    key: "inbox.filters",
    navKey: "inbox",
    group: LISTA,
    label: "Filtros e classificação",
    path: "/inbox",
    requires: "page",
    hint: "Combina status, responsável, tags, canal e período para achar qualquer conversa.",
  },
  {
    key: "inbox.section-waiting",
    navKey: "inbox",
    group: LISTA,
    label: "Aguardando atendimento (a fila)",
    path: "/inbox",
    requires: "page",
    hint: "Conversas novas esperando alguém assumir. O alerta vermelho indica espera longa.",
  },
  {
    key: "inbox.section-mine",
    navKey: "inbox",
    group: LISTA,
    label: "Minhas conversas",
    path: "/inbox",
    requires: "page",
    hint: "As conversas que a pessoa está atendendo.",
  },
  {
    key: "inbox.conversation-item",
    navKey: "inbox",
    group: LISTA,
    label: "Uma conversa da lista",
    path: "/inbox",
    requires: "data",
    hint: "Aponta a primeira conversa da lista. Só aparece quando há ao menos uma conversa.",
  },
  {
    key: "inbox.nav-contacts",
    navKey: "inbox",
    group: LISTA,
    label: "Contatos",
    path: "/inbox",
    requires: "page",
    hint: "Só no layout completo (desktop): a lista de contatos, com busca, importação e ações em lote.",
  },
  {
    key: "inbox.nav-scheduled",
    navKey: "inbox",
    group: LISTA,
    label: "Mensagens agendadas",
    path: "/inbox",
    requires: "page",
    hint: "Só no layout completo (desktop): as mensagens programadas para sair depois.",
  },
  {
    key: "inbox.nav-chatbot",
    navKey: "inbox",
    group: LISTA,
    label: "Monitor do chatbot",
    path: "/inbox",
    requires: "page",
    hint: "Só no layout completo (desktop): acompanha as conversas que o robô está atendendo.",
  },
  {
    key: "inbox.nav-team-chat",
    navKey: "inbox",
    group: LISTA,
    label: "Chat da equipe (canais e mensagens diretas)",
    path: "/inbox",
    requires: "page",
    hint: "Só no layout completo (desktop): os canais e as mensagens diretas entre a equipe, na lateral. Sem o recurso no plano, aparece um convite para conhecer.",
  },
  {
    key: "inbox.nav-video-calls",
    navKey: "inbox",
    group: LISTA,
    label: "Histórico de chamadas de vídeo",
    path: "/inbox",
    requires: "page",
    hint: "Só no layout completo (desktop) e para contas com o complemento de videochamada.",
  },

  // ── Conversa aberta ─────────────────────────────────────────────────────
  {
    key: "inbox.contact-panel",
    navKey: "inbox",
    group: CONVERSA,
    label: "Dados do contato (nome no cabeçalho)",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Clicar no nome do cliente abre o painel com dados, histórico, negócios e integrações.",
    steps: stepsFor("o nome do cliente"),
  },
  {
    key: "inbox.assign-agent",
    navKey: "inbox",
    group: CONVERSA,
    label: "Transferir ou atribuir a conversa",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só no layout completo: passa a conversa para outro agente ou para a fila de um departamento.",
    steps: stepsFor("quem atende a conversa"),
  },
  {
    key: "inbox.ticket-tags",
    navKey: "inbox",
    group: CONVERSA,
    label: "Tags da conversa",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só no layout completo: classifica a conversa com tags para filtrar e medir depois.",
    steps: stepsFor("as tags"),
  },
  {
    key: "inbox.resolve-conversation",
    navKey: "inbox",
    group: CONVERSA,
    label: "Resolver a conversa",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Encerra o atendimento. Agentes só resolvem as conversas deles; supervisores, qualquer uma aberta.",
    steps: stepsFor("o botão Resolver (uma conversa sua, em Minhas conversas)"),
  },
  {
    key: "inbox.mark-won-lost",
    navKey: "inbox",
    group: CONVERSA,
    label: "Marcar como ganho ou perdido",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só no layout completo: registra o resultado da venda e liga ao negócio do Kanban.",
    steps: stepsFor("os botões de ganho e perdido"),
  },
  {
    key: "inbox.voice-call",
    navKey: "inbox",
    group: CONVERSA,
    label: "Ligar (chamada de voz pelo WhatsApp)",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Liga para o cliente pelo WhatsApp. Precisa de chamadas ativas no canal e da permissão do contato.",
    steps: stepsFor("o botão de ligar"),
  },
  {
    key: "inbox.lock-conversation",
    navKey: "inbox",
    group: CONVERSA,
    label: "Tornar a conversa privada (cadeado)",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só no layout completo e para quem pode trancar: só o responsável e administradores veem a conversa.",
    steps: stepsFor("o cadeado"),
  },
  {
    key: "inbox.copy-conversation-link",
    navKey: "inbox",
    group: CONVERSA,
    label: "Copiar o link da conversa",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só no layout completo: copia um link que abre esta conversa direto.",
    steps: stepsFor("o botão de copiar link"),
  },
  {
    key: "inbox.search-in-conversation",
    navKey: "inbox",
    group: CONVERSA,
    label: "Pesquisar dentro da conversa",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Procura uma mensagem específica dentro do histórico desta conversa.",
    steps: stepsFor("a busca dentro da conversa"),
  },
  {
    key: "inbox.channel-tabs",
    navKey: "inbox",
    group: CONVERSA,
    label: "Abas de canal da conversa",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só no layout completo: separa o histórico por canal (WhatsApp, Instagram...).",
    steps: stepsFor("as abas de canal"),
  },
  {
    key: "inbox.files-tab",
    navKey: "inbox",
    group: CONVERSA,
    label: "Aba Arquivos da conversa",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só no layout completo: todas as mídias e documentos trocados nesta conversa.",
    steps: stepsFor("a aba Arquivos"),
  },
  {
    key: "inbox.video-call",
    navKey: "inbox",
    group: CONVERSA,
    label: "Botão de videochamada",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Sem conversa aberta, o ponteiro primeiro manda abrir uma na lista (passo 1) e depois aponta o botão (passo 2).",
    steps: stepsFor("o botão de videochamada"),
  },

  // ── Caixa de mensagem (compositor) ──────────────────────────────────────
  {
    key: "inbox.composer",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Caixa de mensagem",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Onde se escreve a resposta. Enter envia e Shift+Enter quebra a linha.",
    steps: stepsFor("a caixa de mensagem"),
  },
  {
    key: "inbox.send-message",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Enviar mensagem",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Envia ao cliente, ou salva como anotação interna quando a chave de anotação está ligada.",
    steps: stepsFor("o botão de enviar"),
  },
  {
    key: "inbox.internal-note-toggle",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Anotação interna",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só na barra larga: deixa um recado que o cliente nunca vê. Use @ para avisar um colega.",
    steps: stepsFor("a chave de anotação interna"),
  },
  {
    key: "inbox.canned-replies",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Respostas rápidas",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só na barra larga: insere textos prontos, mídias e botões com um atalho. Digite / para buscar.",
    steps: stepsFor("as respostas rápidas"),
  },
  {
    key: "inbox.schedule-message",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Agendar mensagem",
    path: "/inbox",
    requires: "open-ticket",
    hint: "A seta ao lado de Enviar: programa a mensagem para sair depois, por exemplo amanhã às 9h.",
    steps: stepsFor("a seta ao lado de Enviar"),
  },
  {
    key: "inbox.session-bar",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Janela de 24 horas (sessão do WhatsApp)",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Mostra quanto tempo falta para a janela fechar. Depois dela, só template aprovado.",
    steps: stepsFor("a janela de 24 horas"),
  },
  {
    key: "inbox.send-via-channel",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Enviar via (canal de saída)",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Escolhe por qual canal e número a resposta sai.",
    steps: stepsFor("o seletor de canal"),
  },
  {
    key: "inbox.send-template",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Enviar template (janela fechada)",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só aparece quando a janela de 24 horas fechou: reabre a conversa com um template aprovado.",
    steps: stepsFor("o botão Enviar Template (conversa com a janela fechada)"),
  },
  {
    key: "inbox.attach-file",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Anexar arquivo",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Envia imagens, vídeos, áudios e documentos. Também dá para arrastar e colar.",
    steps: stepsFor("o clipe de anexo"),
  },
  {
    key: "inbox.record-audio",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Gravar áudio",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Grava e envia uma mensagem de voz. O navegador pede acesso ao microfone.",
    steps: stepsFor("o microfone"),
  },
  {
    key: "inbox.mention-colleague",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Mencionar um colega (@)",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só na barra larga: avisa um colega dentro de uma anotação interna.",
    steps: stepsFor("o botão @"),
  },
  {
    key: "inbox.ai-summarize",
    navKey: "inbox",
    group: COMPOSITOR,
    label: "Resumir a conversa (IA)",
    path: "/inbox",
    requires: "open-ticket",
    hint: "Só na barra larga: a IA resume o histórico em poucas linhas.",
    steps: stepsFor("o botão de resumo"),
  },

  // ── Menu da lista e preferências ───────────────────────────────────────
  {
    key: "inbox.list-menu",
    navKey: "inbox",
    group: PREFERENCIAS,
    label: "Menu de configurações da lista",
    path: "/inbox",
    requires: "page",
    hint: "A engrenagem (visão completa) ou os três pontos (visão simplificada) no topo da lista: seções, resolução em lote e as preferências.",
  },
  {
    key: "inbox.preferences-menu",
    navKey: "inbox",
    group: PREFERENCIAS,
    label: "Configurações gerais (abrir as preferências)",
    path: "/inbox",
    requires: "panel",
    hint: "O item do menu que abre o modal de Preferências da Inbox. Só aparece com o menu aberto.",
    steps: [ABRIR_MENU],
  },
  preferencia("attendance", "Atendimento", "Modo de distribuição, conversas simultâneas por agente e as regras da fila."),
  preferencia("away-message", "Minha Ausência", "A mensagem automática fora do horário de atendimento, com dias, horas e para quem se aplica."),
  preferencia("tags", "Tags", "Cria e organiza as tags usadas para classificar conversas e contatos."),
  preferencia("canned-replies", "Respostas Rápidas", "Os textos prontos que o atendente insere na conversa com uma barra (/)."),
  preferencia("custom-fields", "Campos Personalizados", "Os campos extras do contato e do negócio que a conta quer registrar."),
  preferencia("api-token", "Token de API", "O token para integrar sistemas à plataforma. Só para administradores.", { minLevel: 50 }),
  preferencia("calling", "Chamadas", "Liga as chamadas de voz e vídeo e define a permissão e o horário. A aba aparece com cadeado se o plano não inclui chamadas.", {
    feature: "calling.whatsapp",
  }),
  preferencia("sales-intelligence", "Sales Intelligence", "As regras que geram os insights de venda na conversa. Só para supervisor ou acima, com o recurso no plano.", {
    feature: "sales.enabled",
    minLevel: 30,
  }),
];
